import { useSessionSelection } from '../useSessionSelection';
/* ============================================================
   Админка · Обращения — письма с формы «Контакты» и фолбэк
   спонсорской модалки (POST /api/support/ticket). Раньше в
   fed-админке раздела не было, и эти сообщения никто не читал.
   ============================================================ */
import { useCallback, useEffect, useState } from 'react';
import { useAppStore } from '../../store';
import { Ic } from '../icons';
import { AdmCard, EmptyRow } from './ui';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch (e) { return String(iso).slice(0, 16); }
};

// Откуда пришло письмо (SupportTicket.page)
const PAGE_LABELS = { contacts: 'Контакты', partners: 'Спонсорство', home: 'Главная' };

const FILTERS = [['all', 'Все'], ['new', 'Новые'], ['read', 'Прочитанные']];

export default function AdmInbox() {
  const fetchSupportTickets = useAppStore((s) => s.fetchSupportTickets);
  const markSupportTicketRead = useAppStore((s) => s.markSupportTicketRead);

  const [tickets, setTickets] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [filter, setFilter] = useSessionSelection('admin-inbox-filter', 'all');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async (status) => {
    setLoading(true);
    const data = await fetchSupportTickets(status === 'all' ? null : status);
    setTickets(Array.isArray(data.tickets) ? data.tickets : []);
    setNewCount(data.new_count || 0);
    setLoading(false);
  }, [fetchSupportTickets]);

  useEffect(() => { load(filter); }, [filter, load]);

  // Разворачивая новое обращение, сразу помечаем прочитанным
  const toggle = async (t) => {
    const next = openId === t.id ? null : t.id;
    setOpenId(next);
    if (next && t.status === 'new') {
      try {
        await markSupportTicketRead(t.id);
        setTickets((list) => list.map((x) => (x.id === t.id ? { ...x, status: 'read' } : x)));
        setNewCount((n) => Math.max(0, n - 1));
      } catch (e) { /* тост уже показан в сторе */ }
    }
  };

  return (
    <div className="fed-admin-view flex flex-col gap-4">
      <div className="flex items-center gap-2.5 flex-wrap">
        {FILTERS.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className="h-9 px-3.5 rounded-[9px] cursor-pointer font-display font-semibold uppercase border"
            style={{
              fontSize: 12, letterSpacing: '.03em',
              color: filter === k ? 'var(--fed-fg)' : 'var(--fed-fg-muted)',
              borderColor: filter === k ? 'var(--fed-line-accent)' : 'var(--fed-line)',
              background: filter === k ? 'rgba(225,18,23,.08)' : 'transparent',
            }}
          >
            {l}
          </button>
        ))}
        <span className="font-mono text-fg-faint" style={{ fontSize: 11 }}>
          {tickets.length} шт.{newCount > 0 && <span style={{ color: 'var(--fed-accent-bright)' }}> · {newCount} новых</span>}
        </span>
      </div>

      <AdmCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="fed-tbl">
            <thead>
              <tr><th>Отправитель</th><th>Контакты</th><th>Откуда</th><th>Дата</th><th>Статус</th></tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} onClick={() => toggle(t)} style={{ cursor: 'pointer' }}>
                  <td data-wide>
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center rounded-lg font-mono font-bold text-fg bg-surface-3 flex-shrink-0" style={{ width: 32, height: 32, fontSize: 13 }}>
                        {(t.name || '?')[0].toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-fg" style={{ fontSize: 14 }}>{t.name || '—'}</div>
                        <div
                          className="text-fg-muted"
                          style={openId === t.id
                            ? { fontSize: 13, marginTop: 4, lineHeight: 1.6, whiteSpace: 'pre-wrap', maxWidth: 620 }
                            : { fontSize: 12.5, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}
                        >
                          {t.message}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Контакты" className="font-mono" style={{ fontSize: 12.5 }}>
                    <div style={{ wordBreak: 'break-all' }}>{t.email || '—'}</div>
                    {t.phone ? <div style={{ marginTop: 3, whiteSpace: 'nowrap' }}>{t.phone}</div> : null}
                  </td>
                  <td data-label="Откуда">
                    <span className="font-mono text-fg-soft bg-surface-3 rounded-md" style={{ fontSize: 11, padding: '4px 8px' }}>
                      {PAGE_LABELS[t.page] || t.page || '—'}
                    </span>
                  </td>
                  <td data-label="Дата" className="font-mono" style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>{fmtDate(t.created_at)}</td>
                  <td data-label="Статус">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full font-mono uppercase"
                      style={{
                        padding: '4px 10px', fontSize: 10, letterSpacing: '.1em',
                        color: t.status === 'new' ? 'var(--fed-info)' : 'var(--fed-fg-muted)',
                        background: t.status === 'new' ? 'rgba(59,165,217,.14)' : 'var(--fed-surface-3)',
                      }}
                    >
                      <Ic name={t.status === 'new' ? 'inbox' : 'check'} size={11} />
                      {t.status === 'new' ? 'Новое' : 'Прочитано'}
                    </span>
                  </td>
                </tr>
              ))}
              {!tickets.length && (
                <EmptyRow colSpan={5}>{loading ? 'Загрузка…' : 'Обращений нет'}</EmptyRow>
              )}
            </tbody>
          </table>
        </div>
      </AdmCard>

      <p className="text-fg-faint" style={{ fontSize: 12, lineHeight: 1.6 }}>
        Сюда попадают письма с формы на странице «Контакты» и заявки на спонсорство,
        которые не дошли в основной канал. Клик по строке разворачивает текст и
        помечает обращение прочитанным.
      </p>
    </div>
  );
}
